import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../Navbar/Navbar';
import '../AdminPanel/AdminPanel.css';
import * as XLSX from 'xlsx';

const ConfigPanel = ({ onNavigate, onLogout, user }) => {
  const [activeTab, setActiveTab] = useState('fichajeautomatico');
  const [fichajesTab, setFichajesTab] = useState('HL'); // Nueva pestaña para HL/HLE
  const [filtroEmpleado, setFiltroEmpleado] = useState('');
  const [fechaDesde, setFechaDesde] = useState(() => new Date().toISOString().slice(0, 10));
  const [fechaHasta, setFechaHasta] = useState('');
  const [filaExpandida, setFilaExpandida] = useState(null);
  const [empleados, setEmpleados] = useState([]);
  const [fichajes, setFichajes] = useState([]);
  const [detalleFichajes, setDetalleFichajes] = useState({});
  const [solicitudes, setSolicitudes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [fichajesAutomaticos, setFichajesAutomaticos] = useState([]);
  const [configEditando, setConfigEditando] = useState(null);
  const [nuevaConfig, setNuevaConfig] = useState({
    usuario: '',
    hora_entrada: '08:00',
    hora_salida: '17:00',
    latitud: '',
    longitud: '',
    direccion: '',
    diasSemana: [1, 2, 3, 4, 5], // Por defecto: Lunes a Viernes
    activo: true
  });

  //hay unas variables de entorno que son el archivo .env y el .env.production
  //en el archivo .env.production se encuentran las variables de entorno para producción
  //en el archivo .env se encuentran las variables de entorno para desarrollo
  //esto setea la ruta del servidor dependiendo de si estamos en producción o desarrollo
  const API_URL = process.env.REACT_APP_API_URL || '';

  const cargarEstadoEmpleados = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/estado-personal`);
      const data = await res.json();
      if (data.success) {
        // Ordenar empleados: primero los que no han fichado (fichado = 0)
        const empleadosOrdenados = data.data.sort((a, b) => {
          if (a.fichado === b.fichado) {
            // Si tienen el mismo estado de fichaje, ordenar por nombre
            return a.nombre_completo.localeCompare(b.nombre_completo);
          }
          // Ordenar por estado de fichaje (0 primero, 1 después)
          return a.fichado - b.fichado;
        });
        setEmpleados(empleadosOrdenados);
      }
    } catch (err) {
      console.error('Error al cargar estado de empleados:', err);
    }
  }, [API_URL]);

  const cargarFichajes = useCallback(async () => {
    try {
      // Enviar typeUser para que el servidor devuelva el formato correcto
      const typeUser = user?.typeUser || 0;
      const res = await fetch(`${API_URL}/api/fichajes?typeUser=${typeUser}`);
      const data = await res.json();
      if (data.success) setFichajes(data.data);
    } catch (err) {
      console.error('Error al cargar fichajes:', err);
    }
  }, [API_URL, user]);

  const cargarDetalleFichajes = async (usuario, fecha) => {
    try {
      const fechaCorta = fecha.slice(0, 10);
      // Incluir la tabla (HL o HLE) en la key para diferenciar
      const tabla = esAdmin && fichajesEsObjeto ? fichajesTab : 'HL';
      const key = `${usuario}_${fechaCorta}_${tabla}`;
      if (detalleFichajes[key]) return;

      const res = await fetch(`${API_URL}/api/fichajes-detalle?usuario=${usuario}&fecha=${fechaCorta}&tabla=${tabla}`);
      const data = await res.json();
      if (data.success) {
        setDetalleFichajes(prev => ({ ...prev, [key]: data.data }));
      }
    } catch (err) {
      console.error('Error al cargar detalle de fichajes:', err);
    }
  };

  const cargarSolicitudes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/solicitudes-todas`);
      const data = await res.json();
      if (data.success) setSolicitudes(data.data);
    } catch (err) {
      console.error('Error al cargar solicitudes:', err);
    }
  }, [API_URL]);

  const cargarUsuarios = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/usuarios`);
      const data = await res.json();
      if (data.success) setUsuarios(data.data);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
    }
  }, [API_URL]);

  const cargarFichajesAutomaticos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/fichajes-automaticos`);
      const data = await res.json();
      if (data.success) setFichajesAutomaticos(data.data);
    } catch (err) {
      console.error('Error al cargar fichajes automáticos:', err);
    }
  }, [API_URL]);

  useEffect(() => {
    if (activeTab === 'fichajeautomatico') {
      cargarUsuarios();
      cargarFichajesAutomaticos();
    }
  }, [activeTab, cargarUsuarios, cargarFichajesAutomaticos]);

  // Resetear fila expandida cuando cambia la pestaña HL/HLE
  useEffect(() => {
    setFilaExpandida(null);
  }, [fichajesTab]);

  const actualizarEstadoSolicitud = async (id, estado) => {
    try {
      const res = await fetch(`${API_URL}/api/actualizar-solicitud`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, estado, autorizadoPor: user?.usuario || 'admin' })
      });
      const data = await res.json();
      if (data.success) cargarSolicitudes();
    } catch (err) {
      console.error('Error al actualizar solicitud:', err);
    }
  };

  // Determinar si el usuario es administrador y los datos vienen en formato {HL: [...], HLE: [...]}
  const esAdmin = user?.typeUser >= 400;
  const fichajesEsObjeto = esAdmin && fichajes && typeof fichajes === 'object' && !Array.isArray(fichajes);
  
  // Obtener los fichajes según la pestaña activa (HL o HLE)
  const fichajesActuales = fichajesEsObjeto 
    ? (fichajes[fichajesTab] || [])
    : (Array.isArray(fichajes) ? fichajes : []);

  const fichajesFiltrados = fichajesActuales.filter((f) => {
    const coincideEmpleado = (f.empleado || f.nombre_completo || '').toLowerCase().includes(filtroEmpleado.toLowerCase());
    const desdeValida = !fechaDesde || f.fecha >= fechaDesde;
    const hastaValida = !fechaHasta || f.fecha <= fechaHasta;
    return coincideEmpleado && desdeValida && hastaValida;
  });

  const solicitudesFiltradas = solicitudes.filter((s) => {
    const coincideEmpleado = s.usuario.toLowerCase().includes(filtroEmpleado.toLowerCase());
    const desdeValida = !fechaDesde || s.fechainicio >= fechaDesde;
    const hastaValida = !fechaHasta || s.fechainicio <= fechaHasta;
    return coincideEmpleado && desdeValida && hastaValida;
  });

  const calcularHorasTrabajadas = (entrada, salida) => {
    if (!entrada || !salida) return '-';
    const [h1, m1] = entrada.split(':').map(Number);
    const [h2, m2] = salida.split(':').map(Number);
    const minutos = (h2 * 60 + m2) - (h1 * 60 + m1);
    const horas = Math.floor(minutos / 60);
    const minutosRest = minutos % 60;
    return `${horas}h ${minutosRest}m`;
  };

  const calcularDiferencia = (entrada, salida) => {
    if (!entrada || !salida) return '-';
    const [h1, m1] = entrada.split(':').map(Number);
    const [h2, m2] = salida.split(':').map(Number);
    const minutos = (h2 * 60 + m2) - (h1 * 60 + m1);
    const diferencia = minutos - 490; // 8h0min = 450min
    const signo = diferencia >= 0 ? '+' : '-';
    const minAbs = Math.abs(diferencia);
    const horas = Math.floor(minAbs / 60);
    const minutosRest = minAbs % 60;
    return `${signo}${horas}h ${minutosRest}m`;
  };

  const calcularTiempoDescanso = (pausa_inicio, pausa_fin) => {
    if (!pausa_inicio || !pausa_fin) return { texto: '-', minutos: 0 };
    const [h1, m1] = pausa_inicio.split(':').map(Number);
    const [h2, m2] = pausa_fin.split(':').map(Number);
    const minutos = (h2 * 60 + m2) - (h1 * 60 + m1);
    const horas = Math.floor(minutos / 60);
    const minutosRest = minutos % 60;
    return {
      texto: `${horas}h ${minutosRest}m`,
      minutos: minutos
    };
  };

  const exportarAExcel = () => {
    const datos = fichajesFiltrados.map(f => ({
      Empleado: f.nombre_completo || f.empleado,
      Fecha: f.fecha,
      Entrada: f.entrada,
      'Pausa Inicio': f.pausa_inicio || '-',
      'Pausa Fin': f.pausa_fin || '-',
      Salida: f.salida,
      HorasTrabajadas: calcularHorasTrabajadas(f.entrada, f.salida),
      Diferencia: calcularDiferencia(f.entrada, f.salida),
      'Tiempo de Descanso': calcularTiempoDescanso(f.pausa_inicio, f.pausa_fin).texto
    }));

    const hoja = XLSX.utils.json_to_sheet(datos);
    const libro = XLSX.utils.book_new();
    const nombreHoja = esAdmin && fichajesEsObjeto ? `Fichajes ${fichajesTab}` : 'Fichajes';
    XLSX.utils.book_append_sheet(libro, hoja, nombreHoja);
    const nombreArchivo = esAdmin && fichajesEsObjeto ? `fichajes_${fichajesTab}.xlsx` : 'fichajes.xlsx';
    XLSX.writeFile(libro, nombreArchivo);
  };

  const agregarConfig = async () => {
    if (!nuevaConfig.usuario || !nuevaConfig.hora_entrada || !nuevaConfig.hora_salida || !nuevaConfig.latitud || !nuevaConfig.longitud) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }
    try {
      const diasSemanaStr = nuevaConfig.diasSemana ? nuevaConfig.diasSemana.join(',') : '1,2,3,4,5';
      const res = await fetch(`${API_URL}/api/fichajes-automaticos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...nuevaConfig,
          dias_semana: diasSemanaStr
        })
      });
      const data = await res.json();
      if (data.success) {
        cargarFichajesAutomaticos();
        setNuevaConfig({
          usuario: '',
          hora_entrada: '08:00',
          hora_salida: '17:00',
          latitud: '',
          longitud: '',
          direccion: '',
          diasSemana: [1, 2, 3, 4, 5],
          activo: true
        });
      } else {
        alert(data.message || 'Error al agregar configuración');
      }
    } catch (err) {
      console.error('Error al agregar configuración:', err);
      alert('Error al agregar configuración');
    }
  };

  const guardarConfig = async () => {
    if (!configEditando.usuario || !configEditando.hora_entrada || !configEditando.hora_salida || !configEditando.latitud || !configEditando.longitud) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }
    try {
      const diasSemanaStr = configEditando.diasSemana ? configEditando.diasSemana.join(',') : (configEditando.dias_semana || '1,2,3,4,5');
      const res = await fetch(`${API_URL}/api/fichajes-automaticos/${configEditando.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...configEditando,
          dias_semana: diasSemanaStr
        })
      });
      const data = await res.json();
      if (data.success) {
        cargarFichajesAutomaticos();
        setConfigEditando(null);
      } else {
        alert(data.message || 'Error al guardar configuración');
      }
    } catch (err) {
      console.error('Error al guardar configuración:', err);
      alert('Error al guardar configuración');
    }
  };

  const eliminarConfig = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta configuración?')) return;
    try {
      const res = await fetch(`${API_URL}/api/fichajes-automaticos/${id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        cargarFichajesAutomaticos();
      } else {
        alert(data.message || 'Error al eliminar configuración');
      }
    } catch (err) {
      console.error('Error al eliminar configuración:', err);
      alert('Error al eliminar configuración');
    }
  };

  return (
    <div className="admin-container">
      <Navbar active="config" onNavigate={onNavigate} onLogout={onLogout} user={user} />
      <main className="admin-main">
        <h2>Configuración</h2>

        <div className="tabs">
          <button
            className={`tab-btn ${activeTab === 'fichajeautomatico' ? 'active' : ''}`}
            onClick={() => setActiveTab('fichajeautomatico')}
          >
            Fichaje Automático
          </button>
        </div>

        {activeTab === 'fichajeautomatico' && (
          <div className="fichajes-panel">
            <div className="admin-new-user-form">
              <h3>{configEditando ? 'Editar Configuración' : 'Nueva Configuración de Fichaje Automático'}</h3>

              <div className="admin-form-group">
                <label htmlFor="config-usuario" className="admin-form-label">Usuario</label>
                <select
                  id="config-usuario"
                  className="admin-form-select"
                  value={configEditando ? configEditando.usuario : nuevaConfig.usuario}
                  onChange={(e) => {
                    if (configEditando) {
                      setConfigEditando({ ...configEditando, usuario: e.target.value });
                    } else {
                      setNuevaConfig({ ...nuevaConfig, usuario: e.target.value });
                    }
                  }}
                >
                  <option value="">Seleccionar usuario</option>
                  {usuarios.map(u => (
                    <option key={u.id} value={u.usuario}>
                      {u.nombre_completo} ({u.usuario})
                    </option>
                  ))}
                </select>
              </div>

              <div className="admin-form-group">
                <label htmlFor="config-hora-entrada" className="admin-form-label">Hora de Entrada</label>
                <input
                  id="config-hora-entrada"
                  className="admin-form-input"
                  type="time"
                  value={configEditando ? configEditando.hora_entrada : nuevaConfig.hora_entrada}
                  onChange={(e) => {
                    if (configEditando) {
                      setConfigEditando({ ...configEditando, hora_entrada: e.target.value });
                    } else {
                      setNuevaConfig({ ...nuevaConfig, hora_entrada: e.target.value });
                    }
                  }}
                />
              </div>

              <div className="admin-form-group">
                <label htmlFor="config-hora-salida" className="admin-form-label">Hora de Salida</label>
                <input
                  id="config-hora-salida"
                  className="admin-form-input"
                  type="time"
                  value={configEditando ? configEditando.hora_salida : nuevaConfig.hora_salida}
                  onChange={(e) => {
                    if (configEditando) {
                      setConfigEditando({ ...configEditando, hora_salida: e.target.value });
                    } else {
                      setNuevaConfig({ ...nuevaConfig, hora_salida: e.target.value });
                    }
                  }}
                />
              </div>

              <div className="admin-form-group">
                <label htmlFor="config-latitud" className="admin-form-label">Latitud</label>
                <input
                  id="config-latitud"
                  className="admin-form-input"
                  type="text"
                  placeholder="Ej: 40.4168"
                  value={configEditando ? configEditando.latitud : nuevaConfig.latitud}
                  onChange={(e) => {
                    if (configEditando) {
                      setConfigEditando({ ...configEditando, latitud: e.target.value });
                    } else {
                      setNuevaConfig({ ...nuevaConfig, latitud: e.target.value });
                    }
                  }}
                />
              </div>

              <div className="admin-form-group">
                <label htmlFor="config-longitud" className="admin-form-label">Longitud</label>
                <input
                  id="config-longitud"
                  className="admin-form-input"
                  type="text"
                  placeholder="Ej: -3.7038"
                  value={configEditando ? configEditando.longitud : nuevaConfig.longitud}
                  onChange={(e) => {
                    if (configEditando) {
                      setConfigEditando({ ...configEditando, longitud: e.target.value });
                    } else {
                      setNuevaConfig({ ...nuevaConfig, longitud: e.target.value });
                    }
                  }}
                />
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Días de la semana para fichaje automático</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '8px' }}>
                  {[
                    { value: 0, label: 'Dom' },
                    { value: 1, label: 'Lun' },
                    { value: 2, label: 'Mar' },
                    { value: 3, label: 'Mié' },
                    { value: 4, label: 'Jue' },
                    { value: 5, label: 'Vie' },
                    { value: 6, label: 'Sáb' }
                  ].map((dia) => {
                    const configActual = configEditando || nuevaConfig;
                    const diasSemana = configActual.diasSemana || (configActual.dias_semana ? configActual.dias_semana.split(',').map(d => parseInt(d.trim(), 10)) : [1, 2, 3, 4, 5]);
                    const estaSeleccionado = diasSemana.includes(dia.value);
                    return (
                      <label
                        key={dia.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          padding: '5px 10px',
                          border: '1px solid #ddd',
                          borderRadius: '5px',
                          backgroundColor: estaSeleccionado ? '#4e58f2' : '#fff',
                          color: estaSeleccionado ? '#fff' : '#333'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={estaSeleccionado}
                          onChange={(e) => {
                            const configActual = configEditando || nuevaConfig;
                            const diasSemana = configActual.diasSemana || (configActual.dias_semana ? configActual.dias_semana.split(',').map(d => parseInt(d.trim(), 10)) : [1, 2, 3, 4, 5]);
                            let nuevosDias;
                            if (e.target.checked) {
                              nuevosDias = [...diasSemana, dia.value].sort();
                            } else {
                              nuevosDias = diasSemana.filter(d => d !== dia.value);
                            }
                            if (configEditando) {
                              setConfigEditando({ ...configEditando, diasSemana: nuevosDias });
                            } else {
                              setNuevaConfig({ ...nuevaConfig, diasSemana: nuevosDias });
                            }
                          }}
                          style={{ marginRight: '5px' }}
                        />
                        {dia.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="admin-form-group">
                <label htmlFor="config-direccion" className="admin-form-label">Dirección</label>
                <input
                  id="config-direccion"
                  className="admin-form-input"
                  type="text"
                  placeholder="Ej: Calle Principal 123, Madrid"
                  value={configEditando ? configEditando.direccion : nuevaConfig.direccion}
                  onChange={(e) => {
                    if (configEditando) {
                      setConfigEditando({ ...configEditando, direccion: e.target.value });
                    } else {
                      setNuevaConfig({ ...nuevaConfig, direccion: e.target.value });
                    }
                  }}
                />
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  <input
                    type="checkbox"
                    checked={configEditando ? configEditando.activo === 1 : nuevaConfig.activo}
                    onChange={(e) => {
                      if (configEditando) {
                        setConfigEditando({ ...configEditando, activo: e.target.checked ? 1 : 0 });
                      } else {
                        setNuevaConfig({ ...nuevaConfig, activo: e.target.checked });
                      }
                    }}
                  />
                  {' '}Activo
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="admin-form-button" onClick={configEditando ? () => guardarConfig() : () => agregarConfig()}>
                  {configEditando ? 'Guardar Cambios' : 'Agregar Configuración'}
                </button>
                {configEditando && (
                  <button 
                    className="admin-form-button" 
                    style={{ backgroundColor: '#6c757d' }}
                    onClick={() => setConfigEditando(null)}
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </div>

            <table className="usuarios-table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Hora Entrada</th>
                  <th>Hora Salida</th>
                  <th>Días de la Semana</th>
                  <th>Ubicación</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {fichajesAutomaticos.map((config) => {
                  const usuario = usuarios.find(u => u.usuario === config.usuario);
                  const diasSemana = config.dias_semana ? config.dias_semana.split(',').map(d => parseInt(d.trim(), 10)) : [1, 2, 3, 4, 5];
                  const nombresDias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
                  const diasTexto = diasSemana.map(d => nombresDias[d]).join(', ');
                  return (
                    <tr key={config.id}>
                      <td>{usuario ? usuario.nombre_completo : config.usuario}</td>
                      <td>{config.hora_entrada}</td>
                      <td>{config.hora_salida}</td>
                      <td>{diasTexto}</td>
                      <td>{config.direccion || `${config.latitud}, ${config.longitud}`}</td>
                      <td>{config.activo === 1 ? '🟢 Activo' : '🔴 Inactivo'}</td>
                      <td>
                        <button onClick={() => {
                          // Convertir dias_semana string a array para edición
                          const configParaEditar = {
                            ...config,
                            diasSemana: diasSemana
                          };
                          setConfigEditando(configParaEditar);
                        }}>Editar</button>
                        <button onClick={() => eliminarConfig(config.id)} style={{ backgroundColor: '#dc3545', color: 'white' }}>Eliminar</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default ConfigPanel;
